import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { Input, Button, Space, Typography, Collapse, Switch } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  CopyOutlined,
  DownOutlined,
  RightOutlined,
  HolderOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DraggableAttributes,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  SectionData,
  QuestionData,
  QuestionType,
} from "../../../../types/survey";
import { QuestionEditor } from "../QuestionEditor";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { useSimpleModal } from "../../Modal/Simple";

const { Text } = Typography;

interface SortableQuestionProps {
  question: QuestionData;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onChange: (updates: Partial<QuestionData>) => void;
  availableQuestions: { id: string; title: string }[];
  hasError?: boolean;
}

const SortableQuestion: React.FC<SortableQuestionProps> = ({
  question,
  index,
  isExpanded,
  onToggle,
  onDelete,
  onCopy,
  onChange,
  availableQuestions,
  hasError,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Collapse
        className="custom-collapse-question"
        activeKey={isExpanded ? [question.id] : []}
        onChange={onToggle}
        bordered
        expandIcon={() => null}
        items={[
          {
            key: question.id,
            label: (
              <QuestionEditor.Header
                question={question}
                isExpanded={isExpanded}
                onDelete={onDelete}
                onCopy={onCopy}
                index={index}
                dragHandleProps={{ attributes, listeners }}
                hasError={hasError}
              />
            ),
            children: (
              <QuestionEditor.Content
                question={question}
                onChange={onChange}
                onDelete={onDelete}
                onCopy={onCopy}
                index={index}
                availableQuestions={availableQuestions}
                error={hasError ? "Question title is required" : undefined}
              />
            ),
          },
        ]}
        styles={{
          root: {
            backgroundColor: "#F5F5F5",
            overflow: "hidden",
            border: "none",
          },
          body: { backgroundColor: "#F5F5F5" },
        }}
      />
    </div>
  );
};

interface HeaderProps {
  section: SectionData;
  isExpanded: boolean;
  onDelete: () => void;
  onCopy?: () => void;
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners: SyntheticListenerMap | undefined;
  };
  hasError?: boolean;
  onChange?: (updates: Partial<SectionData>) => void;
}

const HeaderInner: React.FC<HeaderProps> = ({
  section,
  isExpanded,
  onDelete,
  onCopy,
  dragHandleProps,
  hasError,
  onChange,
}) => {
  const { open, contextHolder } = useSimpleModal();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        background: "#FFFFFF",
        // padding: "0 10px",
        padding: isExpanded ? "17px 10px 0px 20px" : "0px 10px 0px 20px",
      }}
    >
      {isExpanded ? (
        <DownOutlined
          style={{
            color: "#000000E0",
            marginRight: 12,
            fontSize: 12,
          }}
        />
      ) : (
        <RightOutlined
          style={{
            color: "#000000E0",
            marginRight: 12,
            fontSize: 12,
          }}
        />
      )}

      <div
        {...dragHandleProps?.attributes}
        {...dragHandleProps?.listeners}
        style={{
          color: "#999",
          marginRight: 12,
          cursor: "grab",
          display: "flex",
          alignItems: "center",
        }}
      >
        <HolderOutlined />
      </div>

      <Text
        style={{
          fontSize: 14,
          flex: 1,
        }}
      >
        {section.title || "Section title"}
      </Text>
      <Space onClick={(e) => e.stopPropagation()}>
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            marginRight: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Text>Repeatable</Text>
          <Switch
            checked={section.isRepeatable ?? false}
            onChange={(checked) => onChange?.({ isRepeatable: checked })}
          />
        </div>
        <Button
          type="text"
          size="small"
          icon={<CopyOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onCopy?.();
          }}
        />
        <Button
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            open({
              icon: <ExclamationCircleFilled />,
              title: "Delete Section?",
              content: (
                <span>
                  Are you sure you want to delete this section? All questions
                  within this section will be deleted.
                </span>
              ),
              cancelText: "Cancel",
              okText: "Delete",
              okButtonProps: { danger: true },
              onOk: () => onDelete(),
            });
          }}
        />
      </Space>
      {contextHolder}
    </div>
  );
};

interface ContentProps {
  section: SectionData;
  onChange: (updates: Partial<SectionData>) => void;
  onDelete: () => void;
  onAddQuestion: (type: QuestionType) => void;
  onUpdateQuestion: (
    questionId: string,
    updates: Partial<QuestionData>,
  ) => void;
  onDeleteQuestion: (questionId: string) => void;
  onCopyQuestion?: (questionId: string) => void;
  onReorderQuestions?: (questionIds: string[]) => void;
  availableQuestions?: Array<{ id: string; title: string }>;
  sectionError?: string;
  lastQuestionError?: string;
}

const ContentInner: React.FC<ContentProps> = ({
  section,
  onChange,
  onDelete: _onDelete,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onCopyQuestion,
  onReorderQuestions,
  availableQuestions,
  sectionError,
  lastQuestionError,
}) => {
  const [activeKeys, setActiveKeys] = useState<string[]>(
    section.questions.map((q) => q.id),
  );

  const prevQuestionIdsRef = useRef<Set<string>>(
    new Set(section.questions.map((q) => q.id)),
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeQuestion = useMemo(
    () => section.questions.find((q) => q.id === activeId),
    [activeId, section.questions],
  );

  useEffect(() => {
    const currentIds = section.questions.map((q) => q.id);
    const prevIds = prevQuestionIdsRef.current;

    const addedIds = currentIds.filter((id) => !prevIds.has(id));
    if (addedIds.length > 0) {
      setActiveKeys((prev) => {
        const next = new Set(prev);
        addedIds.forEach((id) => next.add(id));
        return Array.from(next);
      });
    }
    setActiveKeys((prev) => prev.filter((id) => currentIds.includes(id)));

    prevQuestionIdsRef.current = new Set(currentIds);
  }, [section.questions]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = section.questions.findIndex((q) => q.id === active.id);
      const newIndex = section.questions.findIndex((q) => q.id === over.id);

      const newQuestions = arrayMove(section.questions, oldIndex, newIndex);
      const reordered = newQuestions.map((q, idx) => ({
        ...q,
        sort: idx,
      }));

      onChange({ questions: reordered });
      onReorderQuestions?.(reordered.map((q) => q.id));
    }
  };

  const handleToggle = (questionId: string) => {
    setActiveKeys((prev) =>
      prev.includes(questionId)
        ? prev.filter((k) => k !== questionId)
        : [...prev, questionId],
    );
  };

  return (
    <div
      style={{
        background: "#ffff",
        borderRadius: 8,
        padding: "0 8px",
      }}
    >
      <div style={{ marginBottom: 24 }}>
        <Text style={{ fontSize: 14, display: "block", marginBottom: 4 }}>
          Section title
        </Text>
        <Input
          value={section.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Enter section title"
          status={sectionError ? "error" : undefined}
          style={{ borderColor: sectionError ? "#ff4d4f" : undefined }}
        />
        {sectionError && (
          <span style={{ color: "#ff4d4f" }}>{sectionError}</span>
        )}
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
      >
        <SortableContext
          items={section.questions.map((q) => q.id)}
          strategy={verticalListSortingStrategy}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              paddingBottom: 16,
            }}
          >
            {section.questions.map((question, index) => (
              <SortableQuestion
                key={question.id}
                question={question}
                index={index}
                isExpanded={activeKeys.includes(question.id)}
                onToggle={() => handleToggle(question.id)}
                onDelete={() => onDeleteQuestion(question.id)}
                onCopy={() => onCopyQuestion?.(question.id)}
                onChange={(updates) => onUpdateQuestion(question.id, updates)}
                availableQuestions={availableQuestions || []}
                hasError={
                  index === section.questions.length - 1 && !!lastQuestionError
                }
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay dropAnimation={null}>
          {activeQuestion ? (
            <div
              style={{
                width: "100%",
                background: "#F5F5F5",
                borderRadius: 6,
                border: "1px solid #E8E8E8",
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                padding: "12px 16px",
                minHeight: 50,
                display: "flex",
                alignItems: "center",
                fontSize: 14,
                fontWeight: 500,
                cursor: "grabbing",
              }}
            >
              {activeQuestion.title || "Untitled question"}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Button icon={<PlusOutlined />} onClick={() => onAddQuestion("text")}>
        Add question to section
      </Button>
    </div>
  );
};

const Header = memo(HeaderInner);
const Content = memo(ContentInner);

export const SectionEditor = {
  Header,
  Content,
};
